import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useStripe } from '../contexts/StripeContext';
import { collection, query, where, getDocs, doc, deleteDoc, updateDoc, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import CreateWorkshop from './CreateWorkshop';
import EditWorkshop from './EditWorkshop';
import CalendarEventManager from './CalendarEventManager';
import CalendarDashboard from './CalendarDashboard';
import WorkshopEmbedding from './WorkshopEmbedding';

function InstructorApp() {
  const { currentUser, logout } = useAuth();
  const { isOnboarded, stripeAccountId, isLoading, startInstructorOnboarding } = useStripe();
  const [workshops, setWorkshops] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({
    totalWorkshops: 0,
    totalBookings: 0,
    totalRevenue: 0,
    upcomingWorkshops: 0
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingWorkshop, setEditingWorkshop] = useState(null);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [showEmbeddingModal, setShowEmbeddingModal] = useState(false);
  const [embeddingWorkshop, setEmbeddingWorkshop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [earnings, setEarnings] = useState(null);
  const [onboardingError, setOnboardingError] = useState('');
  const [showOnboardingPrompt, setShowOnboardingPrompt] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [debugData, setDebugData] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard' or 'calendar'

  useEffect(() => {
    if (currentUser) {
      fetchDashboardData();
      fetchEarnings();
      fetchDebugData();
    }
  }, [currentUser]);

  useEffect(() => {
    // Show onboarding prompt if not onboarded
    if (!isLoading && !isOnboarded && currentUser) {
      setShowOnboardingPrompt(true);
    }
  }, [isLoading, isOnboarded, currentUser]);

  const fetchDebugData = async () => {
    if (!currentUser) return;
    
    try {
      // Fetch instructor document from Firestore
      const instructorDoc = await getDoc(doc(db, 'instructors', currentUser.uid));
      const instructorData = instructorDoc.exists() ? instructorDoc.data() : null;
      
      // Fetch user document from Firestore
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const userData = userDoc.exists() ? userDoc.data() : null;
      
      setDebugData({
        currentUser: {
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: currentUser.displayName
        },
        stripeContext: {
          isOnboarded,
          stripeAccountId,
          isLoading
        },
        instructorDoc: {
          exists: instructorDoc.exists(),
          data: instructorData
        },
        userDoc: {
          exists: userDoc.exists(),
          data: userData
        },
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error fetching debug data:', error);
      setDebugData({
        error: error.message,
        timestamp: new Date().toISOString()
      });
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch workshops
      const workshopsQuery = query(
        collection(db, 'sessions_workshops'),
        where('instructorId', '==', currentUser.uid)
      );
      
      const workshopsSnapshot = await getDocs(workshopsQuery);
      const workshopsData = workshopsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setWorkshops(workshopsData);
      
      // Fetch bookings for instructor's workshops
      const bookingsQuery = query(
        collection(db, 'sessions_bookings'),
        where('instructorId', '==', currentUser.uid)
      );
      
      const bookingsSnapshot = await getDocs(bookingsQuery);
      const bookingsData = bookingsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setBookings(bookingsData);
      
      // Calculate stats
      const confirmedBookings = bookingsData.filter(b => b.status === 'confirmed');
      const totalRevenue = confirmedBookings.reduce((sum, booking) => sum + (booking.totalAmount || 0), 0);
      const upcomingWorkshops = workshopsData.filter(w => new Date(w.startDate) > new Date()).length;
      
      setStats({
        totalWorkshops: workshopsData.length,
        totalBookings: confirmedBookings.length,
        totalRevenue,
        upcomingWorkshops
      });
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEarnings = async () => {
    if (!isOnboarded || !currentUser) return;
    
    try {
      const response = await fetch(`https://klorah-fast-server.onrender.com/api/sessions/instructor-earnings/${currentUser.uid}`);
      if (response.ok) {
        const earningsData = await response.json();
        setEarnings(earningsData);
      }
    } catch (error) {
      console.error('Error fetching earnings:', error);
    }
  };

  const handleStartOnboarding = async () => {
    try {
      setOnboardingError('');
      await startInstructorOnboarding();
    } catch (error) {
      console.error('Error starting onboarding:', error);
      setOnboardingError(error.message || 'Failed to start onboarding process');
    }
  };

  const handleCreatePayout = async () => {
    if (!earnings?.availableBalance || earnings.availableBalance <= 0) {
      alert('No available balance for payout');
      return;
    }

    try {
      const response = await fetch('https://klorah-fast-server.onrender.com/api/sessions/create-instructor-payout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          instructorId: currentUser.uid,
          amount: earnings.availableBalance
        }),
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Payout of $${result.amount} created successfully!`);
        fetchEarnings(); // Refresh earnings
      } else {
        const error = await response.json();
        alert(`Payout failed: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error creating payout:', error);
      alert('Failed to create payout');
    }
  };

  const deleteWorkshop = async (workshopId) => {
    if (!window.confirm('Are you sure you want to delete this workshop?')) {
      return;
    }
    
    try {
      await deleteDoc(doc(db, 'sessions_workshops', workshopId));
      fetchDashboardData(); // Refresh data
    } catch (error) {
      console.error('Error deleting workshop:', error);
      alert('Error deleting workshop');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30';
      case 'cancelled':
        return 'bg-red-500/20 text-red-400 border border-red-500/30';
      default:
        return 'bg-klorah-card/80 text-klorah-text border border-klorah-border';
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return 'TBD';
    }
  };

  const handleCreateWorkshop = () => {
    if (!isOnboarded) {
      setShowOnboardingPrompt(true);
    } else {
      setShowCreateModal(true);
    }
  };

  if (showCreateModal) {
    return <CreateWorkshop onClose={() => setShowCreateModal(false)} />;
  }

  if (editingWorkshop) {
    return <EditWorkshop workshopId={editingWorkshop} onClose={() => setEditingWorkshop(null)} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-klorah-dark flex items-center justify-center">
        <div className="text-white">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-klorah-dark">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Instructor Dashboard</h1>
            <p className="text-klorah-text">Welcome back, {currentUser?.email}</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleCreateWorkshop}
              className="klorah-button"
            >
              Create Workshop
            </button>
            <button
              onClick={logout}
              className="px-4 py-2 bg-klorah-card border border-klorah-border text-klorah-text hover:text-white hover:bg-klorah-border rounded-xl transition-all"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Onboarding Prompt */}
        {showOnboardingPrompt && (
          <div className="mb-6 glass-panel p-6 border border-klorah-primary/30">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white mb-2">
                  Complete Payment Setup Required
                </h3>
                <p className="text-klorah-text mb-4">
                  You need to complete your Stripe onboarding to create workshops and receive payments from students. This is required before you can publish any workshops.
                </p>
                {onboardingError && (
                  <p className="text-red-400 text-sm mb-4">{onboardingError}</p>
                )}
                <div className="flex space-x-3">
                  <button
                    onClick={handleStartOnboarding}
                    className="klorah-button"
                  >
                    Start Payment Setup
                  </button>
                  <button
                    onClick={() => setShowOnboardingPrompt(false)}
                    className="px-4 py-2 bg-klorah-card border border-klorah-border text-klorah-text hover:text-white hover:bg-klorah-border rounded-xl transition-all"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
              <button
                onClick={() => setShowOnboardingPrompt(false)}
                className="ml-4 text-klorah-text hover:text-white text-xl transition-colors"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="border-b border-klorah-border">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'dashboard'
                    ? 'border-klorah-primary text-klorah-primary'
                    : 'border-transparent text-klorah-text hover:text-white hover:border-klorah-border'
                }`}
              >
                Dashboard
              </button>
              <button
                onClick={() => setActiveTab('calendar')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'calendar'
                    ? 'border-klorah-primary text-klorah-primary'
                    : 'border-transparent text-klorah-text hover:text-white hover:border-klorah-border'
                }`}
              >
                Calendar
              </button>
            </nav>
          </div>
        </div>

        {/* Dashboard Content */}
        {activeTab === 'dashboard' && (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="glass-panel p-4">
                <h3 className="text-sm font-medium text-klorah-text">Total Workshops</h3>
                <p className="text-2xl font-bold text-white">{stats.totalWorkshops}</p>
              </div>
              <div className="glass-panel p-4">
                <h3 className="text-sm font-medium text-klorah-text">Total Bookings</h3>
                <p className="text-2xl font-bold text-white">{stats.totalBookings}</p>
              </div>
              <div className="glass-panel p-4">
                <h3 className="text-sm font-medium text-klorah-text">Total Revenue</h3>
                <p className="text-2xl font-bold text-klorah-primary">{formatPrice(stats.totalRevenue)}</p>
              </div>
              <div className="glass-panel p-4">
                <h3 className="text-sm font-medium text-klorah-text">Upcoming Workshops</h3>
                <p className="text-2xl font-bold text-white">{stats.upcomingWorkshops}</p>
              </div>
            </div>

            {/* Earnings Section */}
            {isOnboarded && earnings && (
              <div className="glass-panel p-6 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">Earnings</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-klorah-text">Available Balance</h3>
                    <p className="text-2xl font-bold text-emerald-400">{formatPrice(earnings.availableBalance || 0)}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-klorah-text">Pending Balance</h3>
                    <p className="text-2xl font-bold text-yellow-400">{formatPrice(earnings.pendingBalance || 0)}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-klorah-text">Total Earned</h3>
                    <p className="text-2xl font-bold text-klorah-primary">{formatPrice(earnings.totalEarned || 0)}</p>
                  </div>
                </div>
                {earnings.availableBalance > 0 && (
                  <button
                    onClick={handleCreatePayout}
                    className="mt-4 klorah-button"
                  >
                    Request Payout
                  </button>
                )}
              </div>
            )}

            {/* Workshops Section */}
            <div className="glass-panel mb-6 rounded-xl">
              <div className="p-6 border-b border-klorah-border">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-white tracking-wider">YOUR WORKSHOPS</h2>
                  <button
                    onClick={handleCreateWorkshop}
                    className="klorah-button text-sm"
                  >
                    CREATE WORKSHOP
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                {workshops.length > 0 ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {workshops.map((workshop) => (
                      <div key={workshop.id} className="glass-panel-subtle p-6 rounded-xl border border-klorah-border hover:border-klorah-primary/30 transition-all group">
                        <div className="flex justify-between items-start mb-4">
                          <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full tracking-wider ${
                            workshop.status === 'published' 
                              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                              : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                          }`}>
                            {workshop.status.toUpperCase()}
                          </span>
                          <p className="text-xl font-bold text-klorah-primary">{formatPrice(workshop.price || 0)}</p>
                        </div>
                        
                        <h3 className="text-lg font-semibold text-white mb-3 group-hover:text-klorah-primary transition-colors">
                          {workshop.title}
                        </h3>
                        
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center text-sm text-klorah-text">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {workshop.location}
                          </div>
                          <div className="flex items-center text-sm text-klorah-text">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {formatDate(workshop.startDate)}
                          </div>
                          <div className="flex items-center text-sm text-klorah-text">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            {workshop.currentParticipants || 0} / {workshop.maxParticipants} participants
                          </div>
                        </div>
                        
                        <div className="flex space-x-3 pt-4 border-t border-klorah-border">
                          <button
                            onClick={() => setEditingWorkshop(workshop.id)}
                            className="flex-1 px-4 py-2 bg-klorah-primary/20 text-klorah-primary border border-klorah-primary/30 rounded-xl hover:bg-klorah-primary/30 transition-all text-sm font-medium tracking-wider"
                          >
                            EDIT
                          </button>
                          <button
                            onClick={() => setEmbeddingWorkshop(workshop)}
                            className="flex-1 px-4 py-2 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-xl hover:bg-blue-500/30 transition-all text-sm font-medium tracking-wider"
                          >
                            EMBED
                          </button>
                          <button
                            onClick={() => deleteWorkshop(workshop.id)}
                            className="flex-1 px-4 py-2 bg-red-500/20 text-red-400 border border-red-500/30 rounded-xl hover:bg-red-500/30 transition-all text-sm font-medium tracking-wider"
                          >
                            DELETE
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="glass-panel-subtle p-8 rounded-xl max-w-md mx-auto">
                      <svg className="w-16 h-16 text-klorah-text mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                      <h3 className="text-xl font-semibold text-white mb-3 tracking-wider">NO WORKSHOPS YET</h3>
                      <p className="text-klorah-text mb-6">Create your first workshop to start teaching and earning from students worldwide.</p>
                      <button
                        onClick={handleCreateWorkshop}
                        className="klorah-button"
                      >
                        CREATE YOUR FIRST WORKSHOP
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Bookings */}
            <div className="glass-panel rounded-xl">
              <div className="p-6 border-b border-klorah-border">
                <h2 className="text-2xl font-bold text-white tracking-wider">RECENT BOOKINGS</h2>
              </div>
              
              <div className="p-6">
                {bookings.length > 0 ? (
                  <div className="space-y-4">
                    {bookings.slice(0, 10).map((booking) => (
                      <div key={booking.id} className="glass-panel-subtle p-4 rounded-xl border border-klorah-border hover:border-klorah-primary/30 transition-all">
                        <div className="flex justify-between items-center">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <div className="w-10 h-10 bg-gradient-to-br from-klorah-primary to-klorah-secondary rounded-full flex items-center justify-center">
                                <span className="text-white font-semibold text-sm">
                                  {(booking.customerName || 'A').charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <p className="font-semibold text-white">{booking.customerName || 'Anonymous Student'}</p>
                                <p className="text-sm text-klorah-text">{booking.participantEmail}</p>
                              </div>
                            </div>
                            <div className="ml-13 space-y-1">
                              <div className="flex items-center text-sm text-klorah-text">
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                                {booking.workshopTitle || 'Workshop'}
                              </div>
                              <div className="flex items-center text-sm text-klorah-text">
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                {formatDate(booking.createdAt || booking.workshopDate)}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-klorah-primary mb-2">{formatPrice(booking.totalAmount || 0)}</p>
                            <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full tracking-wider ${
                              booking.status === 'confirmed' 
                                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                                : booking.status === 'pending' 
                                ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                                : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                            }`}>
                              {(booking.status || 'pending').toUpperCase()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="glass-panel-subtle p-8 rounded-xl max-w-md mx-auto">
                      <svg className="w-16 h-16 text-klorah-text mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <h3 className="text-xl font-semibold text-white mb-3 tracking-wider">NO BOOKINGS YET</h3>
                      <p className="text-klorah-text">Once students start booking your workshops, you'll see their information here.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Calendar Content */}
        {activeTab === 'calendar' && (
          <CalendarDashboard />
        )}

        {/* Debug Section */}
        {showDebug && debugData && (
          <div className="mt-6 border border-gray-400 p-4">
            <h2 className="text-xl font-bold text-black mb-4">Debug Information</h2>
            <pre className="bg-gray-50 p-4 border border-gray-300 text-sm overflow-auto">
              {JSON.stringify(debugData, null, 2)}
            </pre>
          </div>
        )}

        {/* Debug Toggle */}
        <div className="mt-6 text-center">
          <button
            onClick={() => setShowDebug(!showDebug)}
            className="text-sm text-gray-500 hover:text-black underline"
          >
            {showDebug ? 'Hide Debug Info' : 'Show Debug Info'}
          </button>
        </div>

        {/* Embedding Modal */}
        {embeddingWorkshop && (
          <WorkshopEmbedding
            workshop={embeddingWorkshop}
            onClose={() => setEmbeddingWorkshop(null)}
          />
        )}
      </div>
    </div>
  );
}

export default InstructorApp; 